import { useEffect, useState } from "react";
import { Link } from "react-router-dom";
import { useContent } from "@/context/ContentContext";

const STORAGE_KEY = "vf_cookie_consent";
const CURRENT_VERSION = 1;

export function getConsent() {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (parsed?.version !== CURRENT_VERSION) return null;
    return parsed;
  } catch {
    return null;
  }
}

function saveConsent(choice) {
  const payload = {
    version: CURRENT_VERSION,
    necessary: true,
    analytics: !!choice.analytics,
    date: new Date().toISOString(),
  };
  localStorage.setItem(STORAGE_KEY, JSON.stringify(payload));
  applyConsent(payload);
  window.dispatchEvent(new CustomEvent("vf:cookie-consent-changed", { detail: payload }));
  return payload;
}

function applyConsent(consent) {
  // Wire PostHog (loaded by index.html) to the user's choice.
  if (typeof window === "undefined") return;
  const ph = window.posthog;
  if (!ph || typeof ph.opt_in_capturing !== "function") return;
  try {
    if (consent.analytics) ph.opt_in_capturing();
    else ph.opt_out_capturing();
  } catch {
    // ignore — PostHog may not be initialised in dev
  }
}

export default function CookieConsent() {
  const { t } = useContent();
  const [visible, setVisible] = useState(false);
  const [showPrefs, setShowPrefs] = useState(false);
  const [analytics, setAnalytics] = useState(true);

  useEffect(() => {
    const existing = getConsent();
    if (!existing) {
      setVisible(true);
    } else {
      applyConsent(existing);
    }
    const reopen = () => {
      const c = getConsent();
      setAnalytics(c ? c.analytics : true);
      setShowPrefs(true);
      setVisible(true);
    };
    window.addEventListener("vf:open-cookie-settings", reopen);
    return () => window.removeEventListener("vf:open-cookie-settings", reopen);
  }, []);

  const acceptAll = () => {
    saveConsent({ analytics: true });
    setVisible(false);
  };
  const rejectAll = () => {
    saveConsent({ analytics: false });
    setVisible(false);
  };
  const savePrefs = () => {
    saveConsent({ analytics });
    setVisible(false);
  };

  if (!visible) return null;

  return (
    <div
      className="fixed inset-x-0 bottom-0 z-50 px-4 pb-4 sm:px-6 sm:pb-6 animate-fade-up"
      data-testid="cookie-consent"
    >
      <div className="mx-auto max-w-5xl bg-ink-800 text-bone-50 border border-gold-500/40 shadow-2xl">
        {!showPrefs ? (
          <div className="p-6 md:p-8 grid md:grid-cols-12 gap-6 items-start">
            <div className="md:col-span-8">
              <div className="eyebrow !text-gold-400 mb-3">Privacy</div>
              <h3 className="font-serif text-2xl md:text-3xl tracking-tight mb-3" data-testid="cookie-consent-title">
                {t("cookies.banner.title")}
              </h3>
              <p className="text-sm md:text-base text-bone-100/75 leading-relaxed">
                {t("cookies.banner.body")}{" "}
                <Link to="/cookie-policy" className="text-gold-400 hover:text-gold-500 underline underline-offset-4">
                  Cookie Policy
                </Link>
                .
              </p>
            </div>
            <div className="md:col-span-4 flex flex-col gap-3">
              <button onClick={acceptAll} className="btn-gold w-full" data-testid="cookie-accept-all">
                {t("cookies.banner.accept_all")}
              </button>
              <button onClick={rejectAll} className="btn-outline-gold w-full" data-testid="cookie-reject-all">
                {t("cookies.banner.reject_all")}
              </button>
              <button
                onClick={() => setShowPrefs(true)}
                className="text-xs uppercase tracking-[0.22em] text-bone-100/60 hover:text-gold-400 py-2"
                data-testid="cookie-preferences"
              >
                {t("cookies.banner.preferences")}
              </button>
            </div>
          </div>
        ) : (
          <div className="p-6 md:p-8" data-testid="cookie-preferences-panel">
            <div className="flex items-start justify-between gap-4 mb-6">
              <div>
                <div className="eyebrow !text-gold-400 mb-3">Privacy</div>
                <h3 className="font-serif text-2xl md:text-3xl tracking-tight">
                  {t("cookies.banner.preferences")}
                </h3>
              </div>
              <button
                onClick={() => setShowPrefs(false)}
                className="text-bone-100/60 hover:text-gold-400 p-2"
                aria-label="Back"
                data-testid="cookie-prefs-back"
              >
                <i className="fa-solid fa-arrow-left" />
              </button>
            </div>

            <div className="space-y-5">
              <div className="border border-ink-600 p-5 bg-ink-700/50">
                <div className="flex items-start justify-between gap-4">
                  <div>
                    <h4 className="font-serif text-lg text-bone-50">{t("cookies.cat.necessary.title")}</h4>
                    <p className="text-sm text-bone-100/70 mt-2 leading-relaxed">
                      {t("cookies.cat.necessary.body")}
                    </p>
                  </div>
                  <span className="uppercase text-[10px] tracking-[0.22em] text-gold-400 whitespace-nowrap mt-1">
                    Always on
                  </span>
                </div>
              </div>

              <div className="border border-ink-600 p-5 bg-ink-700/50">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h4 className="font-serif text-lg text-bone-50">{t("cookies.cat.analytics.title")}</h4>
                    <p className="text-sm text-bone-100/70 mt-2 leading-relaxed">
                      {t("cookies.cat.analytics.body")}
                    </p>
                  </div>
                  <button
                    role="switch"
                    aria-checked={analytics}
                    onClick={() => setAnalytics((v) => !v)}
                    className={`relative w-12 h-6 rounded-full transition-colors mt-1 ${
                      analytics ? "bg-gold-500" : "bg-ink-600"
                    }`}
                    data-testid="cookie-toggle-analytics"
                  >
                    <span
                      className={`absolute top-0.5 left-0.5 w-5 h-5 rounded-full bg-bone-50 transition-transform ${
                        analytics ? "translate-x-6" : "translate-x-0"
                      }`}
                    />
                  </button>
                </div>
              </div>
            </div>

            <div className="flex flex-wrap gap-3 mt-7">
              <button onClick={savePrefs} className="btn-gold" data-testid="cookie-save-prefs">
                {t("cookies.banner.save")}
              </button>
              <button onClick={acceptAll} className="btn-outline-gold" data-testid="cookie-accept-from-prefs">
                {t("cookies.banner.accept_all")}
              </button>
            </div>
          </div>
        )}
      </div>
    </div>
  );
}

export function openCookieSettings() {
  window.dispatchEvent(new CustomEvent("vf:open-cookie-settings"));
}
